import Papa from "papaparse";
import * as XLSX from "xlsx";

import { showWarningNotification } from "./notification";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Sample CSV Format to use while testing
 * "id","email"
 * "1","dibyajyoti49dey@gmail.com"
 * "2","mrflyn6000@gmail.com"
 * "3","sarah.mitchell@company.com"
 * "4","james.kumar92@outlook.com"
 * "5","priya.sharma1995@yahoo.com"
 */
export const extractEmailsFromCSV = (file) => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true, // IMPORTANT: Use headers to map columns
      skipEmptyLines: true,
      complete: (results) => {
        // Find the column key that is most likely the email (case-insensitive check for 'email')
        const headerKeys = Object.keys(results.data[0] || {});
        const emailKey = headerKeys.find((key) => key.toLowerCase().includes("email"));

        if (!emailKey) {
          showWarningNotification("Email column header ('email') not found in CSV file.");
          resolve({ validEmails: [], invalidEmails: [] });
          return;
        }

        // Collect entries only from the identified email column
        const allEntries = results.data
          .map((row) => row[emailKey])
          .map((email) => email?.trim?.())
          .filter((email) => email);

        const validEmails = allEntries.filter((email) => EMAIL_REGEX.test(email));
        const invalidEmails = allEntries.filter((email) => !EMAIL_REGEX.test(email));

        resolve({ validEmails, invalidEmails });
      },
      error: (error) => {
        showWarningNotification(`Error parsing CSV: ${error.message}`);
        resolve({ validEmails: [], invalidEmails: [] });
      }
    });
  });
};

/**
 * Parses an Excel file, reading only from the column identified by the 'email' header.
 * Returns { validEmails: string[], invalidEmails: string[] }
 */
export const extractEmailsFromExcel = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    // Get data as an array of arrays (first array is headers)
    const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    if (data.length === 0) return { validEmails: [], invalidEmails: [] };
    // Identify the column index for 'email'
    const headers = data[0].map((h) => (typeof h === "string" ? h.toLowerCase().trim() : h));
    const emailIndex = headers.findIndex((h) => h === "email");
    if (emailIndex === -1) {
      showWarningNotification("Email column header ('email') not found in Excel file.");
      return { validEmails: [], invalidEmails: [] };
    }
    // Process rows starting from index 1 (skipping headers) and extract only the email column
    const allEntries = data
      .slice(1)
      .map((row) => row[emailIndex])
      .map((email) => email?.toString?.().trim?.())
      .filter((email) => email);
    const validEmails = allEntries.filter((email) => EMAIL_REGEX.test(email));
    const invalidEmails = allEntries.filter((email) => !EMAIL_REGEX.test(email));
    return { validEmails, invalidEmails };
  } catch (error) {
    showWarningNotification(`Error processing Excel file: ${error.message}`);
    return { validEmails: [], invalidEmails: [] };
  }
};
